import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

const DRAG_THRESHOLD = 5;

@Component({
  standalone: true,
  selector: 'app-scroll-row',
  templateUrl: './scroll-row.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./scroll-row.component.scss'],
})
export class ScrollRowComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly track = viewChild.required<ElementRef<HTMLElement>>('track');
  private readonly content = viewChild.required<ElementRef<HTMLElement>>('content');

  private readonly zone = inject(NgZone);

  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);

  private pointerDown = false;
  private dragging = false;
  private suppressClick = false;
  private dragStartX = 0;
  private dragStartScroll = 0;

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const resize = new ResizeObserver(() => this.update());
      resize.observe(this.host.nativeElement);
      resize.observe(this.content().nativeElement);
      destroyRef.onDestroy(() => resize.disconnect());

      const track = this.track().nativeElement;
      this.zone.runOutsideAngular(() => {
        track.addEventListener('scroll', this.onScroll, { passive: true });
        track.addEventListener('pointerdown', this.onPointerDown);
        track.addEventListener('pointermove', this.onPointerMove);
        track.addEventListener('pointerup', this.onPointerUp);
        track.addEventListener('pointercancel', this.onPointerUp);
        track.addEventListener('click', this.onClickCapture, { capture: true });
        track.addEventListener('focusin', this.onFocusIn);
      });
    });
  }

  scroll(direction: 1 | -1): void {
    const { left, right } = this.viewport();
    const cards = Array.from(this.content().nativeElement.children).map((card) => card.getBoundingClientRect());

    const target =
      direction === 1
        ? cards.find((card) => card.right > right + 1)
        : cards.reverse().find((card) => card.left < left - 1);
    if (target) this.reveal(target);
  }

  private update(): void {
    const track = this.track().nativeElement;
    const maxScroll = track.scrollWidth - track.clientWidth;
    this.canScrollLeft.set(track.scrollLeft > 1);
    this.canScrollRight.set(track.scrollLeft < maxScroll - 1);
  }

  private viewport(): { left: number; right: number } {
    const track = this.track().nativeElement;
    const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const view = track.getBoundingClientRect();
    return { left: view.left + padding, right: view.right - padding };
  }

  // Scroll just enough to show the card
  private reveal(card: DOMRect): void {
    const { left, right } = this.viewport();
    const delta = card.left < left ? card.left - left : card.right > right ? card.right - right : 0;
    if (!delta) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track().nativeElement.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  private readonly onScroll = (): void => this.update();

  private readonly onFocusIn = (event: FocusEvent): void => {
    if (this.pointerDown) return;
    const card = Array.from(this.content().nativeElement.children).find((child) =>
      child.contains(event.target as Node),
    );
    if (card) this.reveal(card.getBoundingClientRect());
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    this.suppressClick = false;
    const track = this.track().nativeElement;
    if (event.pointerType !== 'mouse' || event.button !== 0 || track.scrollWidth <= track.clientWidth + 1) return;

    this.pointerDown = true;
    this.dragging = false;
    this.dragStartX = event.clientX;
    this.dragStartScroll = track.scrollLeft;
    window.addEventListener('pointerup', this.resetPress, { once: true });
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.pointerDown) return;

    // Released outside the row
    if (!(event.buttons & 1)) return this.resetPress();

    const track = this.track().nativeElement;
    const dx = event.clientX - this.dragStartX;

    if (!this.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      this.dragging = true;
      track.setPointerCapture(event.pointerId);
      track.classList.add('is-dragging');
    }

    event.preventDefault();
    track.scrollLeft = this.dragStartScroll - dx;
  };

  private readonly resetPress = (): void => {
    this.pointerDown = false;
    this.dragging = false;
    this.track().nativeElement.classList.remove('is-dragging');
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.pointerDown) return;
    this.pointerDown = false;
    if (!this.dragging) return;

    this.dragging = false;
    this.suppressClick = true;
    const track = this.track().nativeElement;
    track.releasePointerCapture(event.pointerId);
    track.classList.remove('is-dragging');
  };

  // Drag release must not open a card
  private readonly onClickCapture = (event: MouseEvent): void => {
    if (!this.suppressClick) return;
    this.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };
}
